<template>
  <div class="push-settings">
    <h2>Avisos de vaga</h2>
    <p>
      Quando sai uma vaga com a sua cara, a gente avisa na hora — na tela do aparelho,
      mesmo com a Recrutei fechada.
    </p>
    <p class="push-settings__scope">
      <b>Este ajuste vale só para este aparelho.</b> Se você usa a Recrutei no celular e no
      computador, precisa ligar em cada um — e desligar aqui não desliga nos outros.
    </p>

    <!-- iPhone numa aba do Safari: a permissão nem chega a ser pedida, então o
         interruptor mentiria. A tela é outra, não a mesma com um aviso a mais. -->
    <div v-if="iosInstall" class="push-settings__notice">
      <p class="push-settings__notice-title">No iPhone e no iPad, instale a Recrutei primeiro</p>
      <p>
        A Apple só permite avisos quando o app está na Tela de Início. É um passo manual,
        e leva menos de um minuto:
      </p>
      <IosInstallSteps
        :style="{ '--push-icon-color': color, '--push-icon-bg': colorSoft }"
      />
    </div>

    <div v-else-if="!supported" class="push-settings__notice">
      <p class="push-settings__notice-title">Este navegador não recebe avisos</p>
      <p>
        Abra a Recrutei no Chrome, no Edge ou no Firefox para ligar os avisos. Numa janela
        anônima eles também não funcionam.
      </p>
    </div>

    <template v-else>
      <div class="control-switch">
        <h2>Receber avisos neste aparelho</h2>
        <div>
          <vs-switch :color="color" :value="enabled" :disabled="loading" @input="handleToggle" />
        </div>
      </div>

      <p v-if="blocked" class="push-settings__blocked">
        O navegador está com os avisos <b>bloqueados</b> para a Recrutei, e por isso não
        pergunta de novo. Toque no cadeado ao lado do endereço, procure por notificações e
        escolha permitir — depois volte aqui e ligue.
      </p>
      <p v-else-if="enabled">
        Os avisos estão <strong>ligados</strong> neste aparelho.
      </p>
      <p v-else>
        Os avisos estão <strong>desligados</strong> neste aparelho. Você continua recebendo
        as vagas por e-mail.
      </p>

      <div v-if="devices.length" class="push-settings__devices">
        <h3>Aparelhos com avisos ligados</h3>
        <ul>
          <li v-for="device in devices" :key="device.uuid">
            <div class="push-settings__device-info">
              <span class="push-settings__device-name">{{ device.device_label || 'Aparelho' }}</span>
              <span v-if="device.uuid === currentUuid" class="push-settings__device-current">
                este aparelho
              </span>
              <span v-if="device.last_used_at" class="push-settings__device-date">
                último aviso em {{ formatDate(device.last_used_at) }}
              </span>
            </div>
            <button
              class="push-settings__device-remove"
              :disabled="loading"
              @click="handleRemove(device)"
            >
              Desligar
            </button>
          </li>
        </ul>
        <p v-if="devicesUnknown" class="push-settings__devices-hint">
          Não conseguimos apontar qual da lista é este aparelho — isso acontece quando os
          dados do navegador foram limpos. O interruptor acima continua desligando este aqui.
        </p>
      </div>
    </template>
  </div>
</template>

<script>
import webPush from '../../services/webPush'
import IosInstallSteps from './IosInstallSteps.vue'
import { clearPushOptOut, markOptedOutOfPush } from '../../utils/pushPreferences'

export default {
  name: 'PushNotificationsSettings',
  components: { IosInstallSteps },
  props: {
    color: {
      type: String,
      default: '#1551b1',
    },
  },
  data() {
    return {
      supported: false,
      iosInstall: false,
      blocked: false,
      enabled: false,
      loading: false,
      devices: [],
      currentUuid: null,
    }
  },
  computed: {
    // O disco atras do numero do passo e a propria cor da empresa a 12%, para o
    // passo a passo nao ficar no azul da Recrutei dentro do portal de outra.
    colorSoft() {
      const c = this.color
      return /^#[0-9a-f]{6}$/i.test(c || '') ? `${c}1F` : 'rgba(21, 81, 177, 0.12)'
    },
    // A lista do servidor não expõe endpoint, e `device_label` não desempata dois
    // Chrome no mesmo Android: sem o uuid guardado, não há como marcar qual é este.
    devicesUnknown() {
      return this.enabled && !this.currentUuid && this.devices.length > 1
    },
  },
  async mounted() {
    this.supported = webPush.isSupported()
    this.iosInstall = webPush.iosNeedsInstall()
    this.blocked = webPush.permission() === 'denied'
    if (!this.supported) return

    try {
      this.enabled = await webPush.syncSubscriptionState()
      this.currentUuid = webPush.currentDeviceUuid()
      await this.loadDevices()
    } catch (e) {
      // Sem conexão com a API: a tela mostra o estado do navegador e segue usável.
    }
  },
  methods: {
    // Tracking nunca pode derrubar o fluxo daqui.
    track(eventName, props = {}) {
      (async () => {
        try {
          const { trackEvent } = await import('@/services/posthog')
          trackEvent(eventName, { source: 'settings', ...props })
        } catch (_) { /* posthog optional */ }
      })()
    },
    async loadDevices() {
      try {
        this.devices = await webPush.listDevices()
      } catch (e) {
        this.devices = []
      }
    },
    formatDate(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString('pt-BR')
    },
    async handleToggle(value) {
      this.loading = true
      try {
        if (value) {
          await webPush.subscribe()
          clearPushOptOut()
          this.enabled = true
          this.track('push_notifications_enabled')
          this.notifySuccess('Avisos ligados', 'Você será avisado neste aparelho sobre novas vagas.')
        } else {
          await webPush.unsubscribeCurrentDevice()
          // Desligar aqui é escolha, não falha: sem esta marca o convite voltaria
          // a aparecer na próxima candidatura, como se a pessoa nunca tivesse dito nada.
          markOptedOutOfPush()
          this.enabled = false
          this.track('push_notifications_disabled')
          this.notifySuccess('Avisos desligados', 'Este aparelho não vai mais receber avisos de vaga.')
        }
        this.currentUuid = webPush.currentDeviceUuid()
        await this.loadDevices()
      } catch (e) {
        this.blocked = webPush.permission() === 'denied'
        this.enabled = await webPush.isSubscribed()
        this.notifyError(e)
      } finally {
        this.loading = false
      }
    },
    async handleRemove(device) {
      this.loading = true
      const isCurrentDevice = device.uuid === this.currentUuid
      try {
        await webPush.removeDevice(device.uuid)
        if (isCurrentDevice) {
          markOptedOutOfPush()
          this.enabled = false
          this.currentUuid = null
        }
        this.track('push_device_removed', { is_current_device: isCurrentDevice })
        await this.loadDevices()
        this.notifySuccess('Aparelho desligado', 'Ele não vai mais receber avisos de vaga.')
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.loading = false
      }
    },
    notifySuccess(title, text) {
      this.$vs.notify({
        title,
        text,
        iconPack: 'feather',
        icon: 'icon-check-circle',
        color: 'success',
      })
    },
    notifyError(error) {
      const code = error && error.message
      const map = {
        DENIED: 'O navegador bloqueou os avisos. Libere pelo cadeado ao lado do endereço.',
        DISMISSED: 'Permissão não concedida. É só tentar de novo quando quiser.',
        UNSUPPORTED: 'Este navegador não suporta avisos.',
        PUSH_SERVICE_UNAVAILABLE: 'Este navegador não disponibiliza serviço de push neste contexto. Tente no navegador do aparelho.',
        NO_VAPID_KEY: 'Não foi possível iniciar os avisos agora. Tente mais tarde.',
      }
      this.$vs.notify({
        title: 'Ops!',
        text: map[code] || 'Não foi possível concluir. Tente novamente.',
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'danger',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.push-settings {
  width: 400px;
  margin: 24px auto;

  h2 {
    font-size: 20px;
    font-weight: 600;
    line-height: 23.44px;
    color: #333;
    margin-bottom: 20px;
  }

  p {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.35;
    color: #696969;
    margin-bottom: 20px;
  }

  .control-switch {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-content: center;
    gap: 8px;
    margin-top: 48px;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 0 16px;
  }

  &__scope {
    color: #4f4f4f;
  }

  &__notice {
    background: #f3f4f6;
    border-radius: 10px;
    padding: 16px;
    margin-top: 24px;

    &-title {
      font-weight: 600;
      color: #333;
      margin-bottom: 8px;
    }
  }

  &__blocked {
    background: rgba(234, 179, 8, 0.12);
    border-radius: 10px;
    padding: 12px 14px;
  }

  &__devices {
    margin-top: 40px;

    h3 {
      font-size: 16px;
      font-weight: 600;
      color: #333;
      margin-bottom: 12px;
    }

    ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 12px 0;
      border-bottom: 1px solid #ededed;
    }

    &-hint {
      margin-top: 12px;
      font-size: 13px;
      color: #696969;
    }
  }

  &__device-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__device-name {
    font-size: 14px;
    color: #333;
  }

  &__device-current {
    font-size: 12px;
    font-weight: 600;
    color: #4169e1;
  }

  &__device-date {
    font-size: 12px;
    color: #9ca3af;
  }

  &__device-remove {
    flex-shrink: 0;
    padding: 8px 14px;
    font-size: 13px;
    color: #4f4f4f;
    background: transparent;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    cursor: pointer;

    &:hover:not(:disabled) {
      border-color: #9ca3af;
      color: #333;
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
}
</style>
